import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Menu,
  MenuItem,
  InputAdornment,
  Avatar,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import axios from '../api/axios';

export default function Interviewers() {
  const navigate = useNavigate();
  const [interviewers, setInterviewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInterviewer, setEditingInterviewer] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuInterviewer, setMenuInterviewer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchInterviewers();
  }, []);

  const fetchInterviewers = async () => {
    try {
      setLoading(true);
      // Since there's no specific endpoint for interviewers yet, we'll use a placeholder
      // You'll need to add this endpoint in the backend
      const response = await axios.get('/users');
      setInterviewers(response.data.data || response.data || []);
      setError('');
    } catch (err) {
      // For now, show a message that this feature needs backend support
      setError('This feature requires additional backend API endpoints. Coming soon!');
      setInterviewers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, interviewer) => {
    setAnchorEl(event.currentTarget);
    setMenuInterviewer(interviewer);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuInterviewer(null);
  };

  const handleOpenDialog = (interviewer = null) => {
    if (interviewer) {
      setEditingInterviewer(interviewer);
      setFormData({
        name: interviewer.name,
        email: interviewer.email,
        password: ''
      });
    } else {
      setEditingInterviewer(null);
      setFormData({
        name: '',
        email: '',
        password: ''
      });
    }
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingInterviewer(null);
    setFormData({ name: '', email: '', password: '' });
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!formData.email.includes('@')) errors.email = 'Invalid email format';
    if (!editingInterviewer && !formData.password) errors.password = 'Password is required';
    if (formData.password && formData.password.length < 6) errors.password = 'Password must be at least 6 characters';
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      if (editingInterviewer) {
        await axios.put(`/users/${editingInterviewer.id}`, formData);
      } else {
        await axios.post('/users', { ...formData, role: 'interviewer' });
      }
      fetchInterviewers();
      handleCloseDialog();
    } catch (err) {
      setFormErrors({ 
        submit: err.response?.data?.message || 'This feature requires additional backend API endpoints' 
      });
    }
  };

  const handleDeleteInterviewer = async (interviewer) => {
    if (!window.confirm(`Are you sure you want to delete ${interviewer.name}?`)) {
      return;
    }

    try {
      await axios.delete(`/users/${interviewer.id}`);
      fetchInterviewers();
      handleMenuClose();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete interviewer');
    }
  };

  const filteredInterviewers = interviewers.filter(interviewer =>
    interviewer.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    interviewer.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading interviewers...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Manage Interviewers
          </Typography>
          <Button
            color="inherit"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ borderColor: 'rgba(255,255,255,0.5)' }}
          >
            Add Interviewer
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {error && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {error}
            <Typography variant="body2" sx={{ mt: 1 }}>
              Note: To enable this feature, add the following routes to your Laravel backend:
              <ul>
                <li>GET /api/users - List all users/interviewers</li>
                <li>POST /api/users - Create new interviewer</li>
                <li>PUT /api/users/:id - Update interviewer</li>
                <li>DELETE /api/users/:id - Delete interviewer</li>
              </ul>
            </Typography>
          </Alert>
        )}

        {/* Search */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ bgcolor: 'white', borderRadius: 1 }}
          />
        </Box>

        {/* Interviewers List */}
        {filteredInterviewers.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <PeopleIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No interviewers found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {searchQuery
                ? 'Try adjusting your search query'
                : 'Add your first interviewer to start managing your team'}
            </Typography>
            {!searchQuery && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Add Interviewer
              </Button>
            )}
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {filteredInterviewers.map((interviewer) => (
              <Grid item xs={12} sm={6} md={4} key={interviewer.id}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'all 0.3s',
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-4px)'
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Avatar
                        sx={{
                          width: 56,
                          height: 56,
                          bgcolor: '#1976d2',
                          fontSize: '1.2rem',
                          fontWeight: 600
                        }}
                      >
                        {getInitials(interviewer.name)}
                      </Avatar>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, interviewer)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {interviewer.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <EmailIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {interviewer.email}
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      <Chip
                        label={interviewer.role?.toUpperCase() || 'INTERVIEWER'}
                        size="small"
                        color="primary"
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleOpenDialog(menuInterviewer)}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => handleDeleteInterviewer(menuInterviewer)}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingInterviewer ? 'Edit Interviewer' : 'Add New Interviewer'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Full Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={!!formErrors.name}
              helperText={formErrors.name}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              error={!!formErrors.email}
              helperText={formErrors.email}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label={editingInterviewer ? 'New Password (leave blank to keep current)' : 'Password'}
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              error={!!formErrors.password}
              helperText={formErrors.password}
            />
            {formErrors.submit && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {formErrors.submit}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editingInterviewer ? 'Update' : 'Add'} Interviewer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

